import {useState} from 'react';
import {hasUserSeenSMPPreviewCtaPulseForAddCover, markUserSeenSMPPreviewCtaPulseForAddCover} from '@Libraries/social-media-wizard-cover-library';
import {SocialMediaWizardStep, socialAccountPostCoverValidations} from '@Components/social-media-wizard/social-media-wizard.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {GraphicsMetaMapType} from '@Components/social-media/publish-params.types';
import {updateNextButtonIcon, updateNextButtonText, updatePost, updatePublishingAction, updateTriggerNext} from '@Components/social-media-wizard/social-media-wizard-reducer';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PostState} from '@Components/social-media/post.vo';
import {buildScheduledPost, deletePrefilledSchedulingDate, getScheduledDateOfPost} from '@Libraries/social-media-wizard-scheduling-library';
import {shouldShowScheduleDetails} from '@Libraries/social-media-wizard-step-library';
import {openSocialPostScheduleModal} from '@Modals/social-post-schedule-modal/social-post-schedule-modal.library';
import {useWizardStep} from '@Components/social-media/social-media.hooks';
import {showSocialMediaSchedulingUpsellDialog} from '@Libraries/social-media-account.library';
import {getCaptionErrorAccountId, getTitleErrorAccountId} from '@Libraries/social-media-params.library';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {isUserPremium} from '@Libraries/user.library';
import {useAppDispatch} from '@/hooks';
import type {CaptionErrorByAccountIds, TitleErrorByAccountIds} from '@Components/social-media/social-media.types';

const NEXT_BUTTON_PUBLISH_ICON = 'icon-publish-arrow';

interface UseWizardCoverCtaPulseOpts {
  currentStep: SocialMediaWizardStep;
  isMobileScreen: boolean;
  designGraphicsMetaMap: GraphicsMetaMapType;
  accountsToPublishTo: SocialAccount[];
}

interface UseWizardCoverCtaPulseResult {
  showPulsatingDotForPreviewCTA: boolean;
  resetPreviewCTAPulsesStates: VoidFunction;
  triggerPreviewCTAPulsesIfNeeded: VoidFunction;
  hidePulsatingDotForPreviewCTAIfNeeded: VoidFunction;
}

interface UseWizardDraftScheduleActionsOpts {
  socialPost: SocialPost | undefined;
  accountIdsToPublishTo: number[];
  captionErrors: CaptionErrorByAccountIds;
  titleErrors: TitleErrorByAccountIds;
  isSmallScreen?: boolean;
}

interface UseWizardDraftScheduleActionsResult {
  isDateTimePickerVisible: boolean;
  setIsDateTimePickerVisible: (visible: boolean) => void;
  onSaveAsDraft: VoidFunction;
  onSchedule: VoidFunction;
  onScheduleAt: (dateTime: Date) => void;
  openScheduleModal: VoidFunction;
  onSchedulingDone: VoidFunction;
  onDeleteSchedule: VoidFunction;
}

export function useWizardCoverCtaPulse({currentStep, isMobileScreen, designGraphicsMetaMap, accountsToPublishTo}: UseWizardCoverCtaPulseOpts): UseWizardCoverCtaPulseResult {
  const [showPulsatingDotForPreviewCTA, setShowPulsatingDotForPreviewCTA] = useState(false);

  const isPostEligibleForCover = (): boolean => {
    const isSingleGraphic = Object.values(designGraphicsMetaMap).length === 1 && Object.values(Object.values(designGraphicsMetaMap)[0]).length === 1;

    if (!isSingleGraphic) {
      return false;
    }

    const isVideo = Object.values(Object.values(designGraphicsMetaMap)[0])[0].isVideo;
    if (!isVideo) {
      return false;
    }

    const accountsSupportingPostCoverToPublishTo = accountsToPublishTo.filter((account) => socialAccountPostCoverValidations[account.type]?.doesSupportCover);

    return accountsSupportingPostCoverToPublishTo.length >= 1;
  };

  const showNewFeaturePulseForAddCoverCTA = (): boolean => {
    return currentStep === SocialMediaWizardStep.ADD_DETAILS && isPostEligibleForCover();
  };

  const shouldTriggerPreviewCTAPulses = (): boolean => {
    if (!isMobileScreen) {
      return false;
    }

    return showNewFeaturePulseForAddCoverCTA();
  };

  const markUserSeenSMPPreviewCtaPulse = (): void => {
    if (showNewFeaturePulseForAddCoverCTA()) {
      void markUserSeenSMPPreviewCtaPulseForAddCover();
    }
  };

  const updateShowPulsatingDotForPreviewCtaState = async (): Promise<void> => {
    let hasUserSeen = true;

    if (showNewFeaturePulseForAddCoverCTA()) {
      hasUserSeen = await hasUserSeenSMPPreviewCtaPulseForAddCover();
    }

    if (hasUserSeen) {
      return;
    }

    setShowPulsatingDotForPreviewCTA(true);
  };

  const resetPreviewCTAPulsesStates = (): void => {
    if (!isMobileScreen) {
      return;
    }

    setShowPulsatingDotForPreviewCTA(false);
  };

  const triggerPreviewCTAPulsesIfNeeded = (): void => {
    if (!shouldTriggerPreviewCTAPulses()) {
      return;
    }

    void updateShowPulsatingDotForPreviewCtaState();
  };

  const hidePulsatingDotForPreviewCTAIfNeeded = (): void => {
    if (!shouldTriggerPreviewCTAPulses() || !showPulsatingDotForPreviewCTA) {
      return;
    }

    setShowPulsatingDotForPreviewCTA(false);
    markUserSeenSMPPreviewCtaPulse();
  };

  return {
    showPulsatingDotForPreviewCTA,
    resetPreviewCTAPulsesStates,
    triggerPreviewCTAPulsesIfNeeded,
    hidePulsatingDotForPreviewCTAIfNeeded,
  };
}

export function useWizardDraftScheduleActions({
  socialPost,
  accountIdsToPublishTo,
  captionErrors,
  titleErrors,
  isSmallScreen = false,
}: UseWizardDraftScheduleActionsOpts): UseWizardDraftScheduleActionsResult {
  const dispatch = useAppDispatch();
  const wizardStep = useWizardStep();
  const [isDateTimePickerVisible, setIsDateTimePickerVisible] = useState(false);

  const unschedulePost = (): void => {
    if (socialPost === undefined) {
      return;
    }

    void window.PMW.write(window.PMW.util.site_url('socialmedia/unschedulePost'), {idPost: socialPost.id});
  };

  const doDeleteScheduling = (): void => {
    if (socialPost?.publishOn && socialPost.timeZoneOffset !== null) {
      const {publishOn: _, timeZoneOffset: __, state: ___, ...unscheduledPost} = socialPost;
      const draftPost: SocialPost = {...unscheduledPost, state: PostState.DRAFT};
      deletePrefilledSchedulingDate();
      unschedulePost();
      dispatch(updatePost(draftPost));
    }
  };

  const onDeleteSchedule = (): void => {
    if (socialPost?.publishOn && socialPost.timeZoneOffset !== null) {
      doDeleteScheduling();
      dispatch(updateNextButtonText(window.i18next.t('pmwjs_publish')));
      dispatch(updateNextButtonIcon(NEXT_BUTTON_PUBLISH_ICON));
    }
  };

  const onSaveAsDraft = (): void => {
    doDeleteScheduling();

    if (getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null || getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) != null) {
      dispatch(updateTriggerNext());
      return;
    }

    dispatch(updatePublishingAction(SocialPostPublishingAction.SAVE));
    dispatch(updateTriggerNext());
  };

  const onSchedulingDone = (): void => {
    if (getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors) !== null || getTitleErrorAccountId(accountIdsToPublishTo, titleErrors) != null) {
      dispatch(updateTriggerNext());
      return;
    }

    dispatch(updatePublishingAction(SocialPostPublishingAction.SCHEDULE));
    dispatch(updateTriggerNext());
  };

  const onScheduleAt = (dateTime: Date): void => {
    if (socialPost === undefined) {
      return;
    }

    dispatch(updatePost(buildScheduledPost(socialPost, dateTime)));
    dispatch(updateNextButtonText(window.i18next.t('pmwjs_schedule')));
    dispatch(updateNextButtonIcon(NEXT_BUTTON_PUBLISH_ICON));
    deletePrefilledSchedulingDate();
  };

  const openScheduleModal = (): void => {
    openSocialPostScheduleModal({
      scheduledDate: getScheduledDateOfPost(socialPost),
      isEditingExistingSchedule: shouldShowScheduleDetails(socialPost, wizardStep),
      onScheduleAt,
      onDeleteSchedule,
    });
  };

  const onSchedule = (): void => {
    if (!isUserPremium()) {
      showSocialMediaSchedulingUpsellDialog();
      return;
    }

    if (isSmallScreen) {
      openScheduleModal();
      return;
    }

    setIsDateTimePickerVisible(true);
  };

  return {
    isDateTimePickerVisible,
    setIsDateTimePickerVisible,
    onSaveAsDraft,
    onSchedule,
    onScheduleAt,
    openScheduleModal,
    onSchedulingDone,
    onDeleteSchedule,
  };
}
